import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import React from "react";
import { twMerge } from "tailwind-merge";
import { CopyToClipboard } from "./CopyToClipboard";

export type TagSeverity =
  | "critical"
  | "high"
  | "medium"
  | "low"
  | "info"
  | "exposure";
export type TagVariant = "risk" | "dashed" | "info" | "filter";

// Base props without variant since each component will be its own variant
interface BaseTagProps {
  className?: string;
  onClick?: () => void;
  size?: "xs" | "sm" | "md" | "lg";
  copy?: boolean;
  textToCopy?: string;
}

// Specific props for each variant
export interface RiskTagProps extends BaseTagProps {
  severity: TagSeverity;
  prefix?: string;
  onDropdown?: () => void;
}

export interface DashedTagProps extends BaseTagProps {
  active?: boolean;
  label: string;
}

export interface InfoTagProps extends BaseTagProps {
  label: string;
  icon: React.ReactNode;
}

export interface FilterTagProps extends BaseTagProps {
  icon?: React.ReactNode;
  label: string;
  active?: boolean;
}

// Add size classes helper
const getSizeClasses = (size: "xs" | "sm" | "md" | "lg" = "md") => {
  const sizes = {
    xs: "py-1 px-2 text-[10px]",
    sm: "py-1.5 px-3 text-xs",
    md: "py-2 px-4 text-xs",
    lg: "py-2.5 px-5 text-sm",
  };
  return sizes[size];
};

// Base component with common styles
export const BaseTag: React.FC<
  BaseTagProps & { children: React.ReactNode }
> = ({
  className,
  onClick,
  children,
  size = "md",
  copy = false,
  textToCopy,
}) => (
  <div
    className={twMerge(
      "w-fit shrink-0 border border-chariot-stroke inline-flex text-chariot-text-primary justify-center items-center rounded-full font-semibold leading-3 transition-colors duration-200",
      getSizeClasses(size),
      className
    )}
    onClick={onClick}
  >
    {copy ? (
      <CopyToClipboard
        className="flex items-center"
        iconClassName="size-3 text-chariot-text-secondary"
        textToCopy={textToCopy}
        alwaysShowCopyIcon={true}
      >
        {children}
      </CopyToClipboard>
    ) : (
      children
    )}
  </div>
);

// Individual variant components
export const RiskTag: React.FC<RiskTagProps> = ({
  severity = "info",
  className,
  prefix,
  onClick,
  onDropdown,
  size,
}) => {
  const severityClasses = {
    critical: "bg-chariot-status-critical border-chariot-stroke-critical text-white",
    high: "bg-chariot-status-high border-chariot-stroke-high text-white",
    medium: "bg-chariot-status-medium border-chariot-stroke-medium text-white",
    low: "bg-chariot-status-low border-chariot-stroke-low text-white",
    info: "bg-chariot-status-info border-chariot-stroke-info text-white",
    exposure: "bg-chariot-status-info border-chariot-stroke-info text-white",
  };

  const handleClick = () => {
    if (onClick) onClick();
    if (onDropdown) onDropdown();
  };

  return (
    <BaseTag
      className={twMerge(
        severityClasses[severity],
        className,
        onClick || onDropdown ? "cursor-pointer" : "",
        prefix ? "space-x-2 p-0 overflow-hidden" : ""
      )}
      onClick={handleClick}
      size={size}
    >
      {prefix && (
        <div
          className={twMerge(
            severityClasses[severity],
            "border-0 border-r py-2 px-4"
          )}
        >
          {prefix}
        </div>
      )}
      <span className={twMerge("uppercase", prefix ? "pl-2 pr-4" : "px-0")}>
        {severity}
      </span>
      {onDropdown && <ChevronDownIcon className="ml-1 stroke-[2.5px] size-4" />}
    </BaseTag>
  );
};

export const TextRiskTag: React.FC<RiskTagProps> = ({
  severity = "info",
  className,
  onClick,
  size,
}) => {
  const severityClasses = {
    critical: "bg-chariot-status-critical border-chariot-stroke-critical text-white",
    high: "bg-chariot-status-high border-chariot-stroke-high text-white",
    medium: "bg-chariot-status-medium border-chariot-stroke-medium text-white",
    low: "bg-chariot-status-low border-chariot-stroke-low text-white",
    info: "bg-chariot-status-info border-chariot-stroke-info text-white",
    exposure: "bg-chariot-status-info border-chariot-stroke-info text-white",
  };

  return (
    <BaseTag
      className={twMerge(
        "flex items-center space-x-2",
        severityClasses[severity],
        className,
        onClick ? "cursor-pointer" : ""
      )}
      onClick={onClick}
      size={size}
    >
      <span className="uppercase">{severity}</span>
      <div
        className={twMerge("size-2 rounded-full", severityClasses[severity])}
      />
    </BaseTag>
  );
};

export interface DashedTagProps extends BaseTagProps {
  active?: boolean;
  label: string;
  icon?: React.ReactNode;
}

export const DashedTag: React.FC<DashedTagProps> = ({
  label = "",
  active,
  className,
  onClick,
  size,
  icon,
  copy,
  textToCopy,
}) => (
  <BaseTag
    className={twMerge(
      "border-dashed border-2 text-chariot-text-secondary uppercase",
      active ? "border-chariot-brand-primary text-chariot-brand-primary" : "",
      className
    )}
    onClick={onClick}
    size={size}
    copy={copy}
    textToCopy={textToCopy ?? label}
  >
    {icon && <span className="mr-2">{icon}</span>}
    <span>{label}</span>
  </BaseTag>
);

export const InfoTag: React.FC<InfoTagProps> = ({
  label = "",
  icon,
  className,
  onClick,
  size,
  copy,
}) => (
  <BaseTag
    className={twMerge(
      "bg-chariot-transparent border-chariot-stroke text-chariot-text-primary flex space-x-2",
      className
    )}
    onClick={onClick}
    size={size}
    copy={copy}
  >
    <span>{icon}</span>
    <span className="whitespace-nowrap">{label}</span>
  </BaseTag>
);

export const FilterTag: React.FC<FilterTagProps> = ({
  label = "",
  active,
  className,
  onClick,
  size,
  copy,
}) => (
  <BaseTag
    className={twMerge(
      "text-chariot-text-secondary bg-chariot-transparent border-chariot-stroke flex space-x-2",
      active
        ? "border-chariot-brand-primary bg-chariot-background-secondary border-2"
        : "",
      className
    )}
    onClick={onClick}
    size={size}
    copy={copy}
  >
    <span>{label}</span>
    <button
      className={twMerge(
        "ml-2 rounded-full bg-chariot-text-secondary size-4 inline-flex justify-center items-center transition-transform duration-200",
        active ? "rotate-45" : "rotate-0"
      )}
      onClick={onClick}
    >
      <PlusIcon className="stroke-[3px] size-3 text-chariot-background-primary" />
    </button>
  </BaseTag>
);
